import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Download, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useState } from 'react';

const AGREEMENT_LABELS: Record<string, string> = {
  beta_agreement_signed: 'Beta Testing Agreement (Executed)',
  privacy_policy: 'Privacy Policy Acceptance',
  tos_shop: 'Shop Terms of Service Acceptance',
  tos_passport: 'Passport Terms of Service Acceptance',
  beta_agreement_ack: 'Beta Agreement Acknowledgement',
};

export const LegalDocumentsPanel = () => {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState<string | null>(null);

  const { data: documents, isLoading } = useQuery({
    queryKey: ['legal-documents', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_acceptances')
        .select('id, agreement_type, agreement_version, accepted_at, storage_path, signature_method')
        .eq('user_id', user!.id)
        .order('accepted_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const handleDownload = async (storagePath: string, agreementType: string) => {
    setDownloading(storagePath);
    try {
      const { data, error } = await supabase.storage
        .from('legal-executed')
        .createSignedUrl(storagePath, 60);

      if (error) throw error;
      if (!data?.signedUrl) throw new Error('No download URL generated');

      window.open(data.signedUrl, '_blank');
    } catch (err: any) {
      console.error('Download error:', err);
      toast({
        title: 'Download Failed',
        description: 'Could not generate download link. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDownloading(null);
    }
  };

  const downloadableDocuments = documents?.filter(d => d.storage_path) || [];
  const acceptanceRecords = documents?.filter(d => !d.storage_path) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Legal Documents
        </CardTitle>
        <CardDescription>Your signed agreements and legal acceptances</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !documents?.length ? (
          <p className="text-sm text-muted-foreground">No legal documents found.</p>
        ) : (
          <div className="space-y-4">
            {downloadableDocuments.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Signed Agreements</h4>
                {downloadableDocuments.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3 rounded-md border border-border">
                    <div>
                      <p className="text-sm font-medium">
                        {AGREEMENT_LABELS[doc.agreement_type] || doc.agreement_type}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Signed {new Date(doc.accepted_at).toLocaleDateString()} · v{doc.agreement_version}
                        {doc.signature_method && ` · ${doc.signature_method}`}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(doc.storage_path!, doc.agreement_type)}
                      disabled={downloading === doc.storage_path}
                    >
                      {downloading === doc.storage_path ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-1" />
                      )}
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {acceptanceRecords.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Acceptance Records</h4>
                {acceptanceRecords.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3 rounded-md border border-border">
                    <div>
                      <p className="text-sm font-medium">
                        {AGREEMENT_LABELS[doc.agreement_type] || doc.agreement_type}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Accepted {new Date(doc.accepted_at).toLocaleDateString()} · v{doc.agreement_version}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
